import type { Facility } from '@reliance/types/models/Facility';
import type { Schedule } from '@reliance/types/models/Schedule';
import type { ScheduleEntry } from '@reliance/types/models/Schedule';
import React, { useCallback, useMemo, useState } from 'react';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import api from '../common/api';
import Add from '../schedule/Add';
import Table from '../schedule/Table';
import type { ScheduleEditorModel } from '../schedule/Table';
import { useSchedulesByFacilityId } from '../hooks/Schedule';
import { useNotifyTabLoading } from '../hooks/useTabLoadingReporter';
import { useAlerts } from '../alert/AlertContext';

type LocalScheduleDraft = {
  clientKey: string;
  type: string;
  entries: ScheduleEntry[];
};

type TabItem = Schedule | LocalScheduleDraft;

const isLocalDraft = (s: TabItem): s is LocalScheduleDraft => 'clientKey' in s && (s as LocalScheduleDraft).clientKey != null;

type FacilitySchedulesTabProps = {
  facility: Facility;
};

const FacilitySchedulesTab: React.FC<FacilitySchedulesTabProps> = ({ facility }) => {
  const alerts = useAlerts();
  const { result, loading, error, execute } = useSchedulesByFacilityId(facility.id);
  const [drafts, setDrafts] = useState<LocalScheduleDraft[]>([]);
  const [updating, setUpdating] = useState(false);
  const [tabKey, setTabKey] = useState<string | null>(null);

  const items: TabItem[] = useMemo(() => [...(result ?? []), ...drafts], [result, drafts]);

  useNotifyTabLoading('schedules', loading || updating);

  const tabItems = useMemo(
    () =>
      items.map((s) => ({
        item: s,
        key: String(isLocalDraft(s) ? s.clientKey : s.id),
        title: s.type as string,
      })),
    [items]
  );

  const handleAdd = useCallback((payload: { type: string }) => {
    const clientKey = `draft-${Date.now()}`;
    setDrafts((d) => [...d, { clientKey, type: payload.type, entries: [] }]);
    setTabKey(clientKey);
  }, []);

  const handleUpdateSchedule = useCallback(
    async (schedule: TabItem, payload: { type: string; entries: { day: number; start: string; stop: string }[] }) => {
      setUpdating(true);
      try {
        await api.post('/schedule', {
          facilityId: facility.id,
          type: payload.type,
          entries: payload.entries,
        });
        alerts.success('The schedule was successfully updated');
        await execute();
        if (isLocalDraft(schedule)) {
          setDrafts((d) => d.filter((x) => x.clientKey !== schedule.clientKey));
        }
      } catch {
        alerts.error('An unexpected error occurred while updating the schedule');
      } finally {
        setUpdating(false);
      }
    },
    [execute, facility.id]
  );

  if (error) {
    return <div className="alert alert-danger">An unexpected error occurred while retrieving the facility schedules</div>;
  }

  return (
    <div className="jarviswidget">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="mb-2">
              <Add onAdd={handleAdd} />
            </div>
            {loading && <p>Loading schedules...</p>}
            {!loading && tabItems.length > 0 && (
              <Tabs
                activeKey={tabKey ?? tabItems[0]?.key}
                id="facility-schedules-tabs"
                onSelect={(k) => k != null && setTabKey(k)}>
                {tabItems.map(({ item, key, title }) => {
                  const editor: ScheduleEditorModel = isLocalDraft(item)
                    ? { clientKey: item.clientKey, type: item.type, entries: item.entries }
                    : { id: item.id, type: item.type as string, entries: item.entries };
                  return (
                    <Tab key={key} eventKey={key} title={title}>
                      <div className="jarviswidget">
                        <div>
                          <Table
                            currentSchedule={editor}
                            updating={updating}
                            onUpdateSchedule={(payload) => handleUpdateSchedule(item, payload)}
                          />
                        </div>
                      </div>
                    </Tab>
                  );
                })}
              </Tabs>
            )}
            {!loading && tabItems.length === 0 && <p className="text-muted">No schedules yet. Add a schedule type above.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacilitySchedulesTab;
